import React, { useState, useEffect } from "react";
import AdminSidebar from "./AdminSidebar";
import AdminHeader from "./AdminHeader";
import ProfileSettings from "./ProfileSettings";
import { TAB_CONFIG as ADMIN_TABS } from "./helper/tabConfig";
import { DeleteAxios, GetAxios } from "../../helpers/axiosHelpers";
import useAuthStore from "../../store/authStore";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

const AdminDashboard = () => {
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation & UI State
  const [isOpen, setIsOpen] = useState(false); // Sidebar open/close state
  const [showProfileSettings, setShowProfileSettings] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Data State
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });

  const fetchGlobalData = async (page = 1) => {
    try {
      setLoading(true);
      // Passing pagination as query params
      const res = await GetAxios(`/application?page=${page}&limit=10`);

      if (res.success) {
        setSubmissions(res.data.applications);
        setPagination(res.data.meta); // Save totalPages, total, etc.
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const deleteSubmission = async (id) => {
    // 1. Create a promise-based toast that tracks the API call
    const deletePromise = DeleteAxios(`/application/${id}`);

    toast.promise(deletePromise, {
      loading: "Deleting submission...",
      success: (res) => {
        // Check if your custom API structure returned success: false
        if (res && res.success === false) {
          throw new Error(res.message || "Failed to delete submission");
        }

        // Refresh your UI data
        fetchGlobalData(pagination.page);
        return "Submission deleted successfully!";
      },
      error: (err) => {
        // Fallback to a generic message if the server didn't provide one
        return err.message || "An error occurred while deleting.";
      },
    });
  };

  // Re-fetch when dashboard mounts
  useEffect(() => {
    fetchGlobalData(1);
  }, []);

  // Derive active tab from the URL: /admin -> dashboard, /admin/testimonials -> testimonials
  const pathSegments = location.pathname
    .replace(/^\/admin\/?/, "")
    .split("/")
    .filter(Boolean);
  const activeTab = ADMIN_TABS[pathSegments[0]]
    ? pathSegments[0]
    : "dashboard";

  // Only the data-heavy tabs wait for the global submissions fetch
  const needsGlobalData = activeTab === "dashboard" || activeTab === "submissions";

  // Get the active view component from the Registry
  const ActiveView = ADMIN_TABS[activeTab]?.component;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex font-sans overflow-x-hidden">
      {/* 1. Sidebar */}
      <AdminSidebar
        activeTab={activeTab}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onLogout={handleLogout}
      />

      {/* 2. Main Body */}
      <main className="flex-1 lg:ml-72 min-h-screen flex flex-col relative">
        <AdminHeader
          title={ADMIN_TABS[activeTab].label}
          subtitle={ADMIN_TABS[activeTab].description}
          setIsOpen={setIsOpen} // For mobile menu button
          onProfileClick={() => setShowProfileSettings(true)}
        />

        <div className="p-4 md:p-8 lg:p-12 max-w-[1600px] mx-auto w-full flex-1">
          {needsGlobalData && loading ? (
            <div className="h-[60vh] flex flex-col items-center justify-center opacity-30">
              <div className="w-10 h-10 border-4 border-subidha-blue border-t-transparent rounded-full animate-spin mb-4" />
              <p className="font-black uppercase tracking-[0.3em] text-[10px]">
                Syncing Engine...
              </p>
            </div>
          ) : (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
              {ActiveView && (
                <ActiveView
                  submissions={submissions}
                  setSubmissions={setSubmissions}
                  refreshData={fetchGlobalData}
                  onDelete={deleteSubmission}
                />
              )}
            </div>
          )}
        </div>
      </main>

      {/* Profile Settings Modal */}
      {showProfileSettings && (
        <ProfileSettings onClose={() => setShowProfileSettings(false)} />
      )}
    </div>
  );
};

export default AdminDashboard;
